import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Save, Plus, X, Ban } from "lucide-react";
import { toast } from "sonner";
import AdminNav from "@/components/AdminNav";
import api from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";

const DAYS = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];

const PRESET_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM",
  "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM",
];

export default function AdminAvailability() {
  const [me, setMe] = useState(null);
  const [weekly, setWeekly] = useState({});
  const [blocked, setBlocked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState();
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [mRes, aRes] = await Promise.all([api.get("/auth/me"), api.get("/availability")]);
      setMe(mRes.data);
      setWeekly(aRes.data.weekly || {});
      setBlocked(aRes.data.blocked_dates || []);
    } catch (e) {
      toast.error("Could not load availability");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleSlot = (day, slot) => {
    setWeekly((w) => {
      const cur = w[day] || [];
      const has = cur.includes(slot);
      return { ...w, [day]: has ? cur.filter((s) => s !== slot) : [...cur, slot].sort(sortSlot) };
    });
  };

  const saveWeekly = async () => {
    setSaving(true);
    try {
      await api.patch("/availability/weekly", { days: weekly });
      toast.success("Weekly hours saved");
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const blockSelected = async () => {
    if (!date) return toast.error("Pick a date first");
    setBusy(true);
    try {
      const iso = date.toISOString().slice(0, 10);
      await api.post("/availability/block", { date: iso });
      setBlocked((b) => (b.includes(iso) ? b : [...b, iso].sort()));
      toast.success(`Blocked ${iso}`);
    } catch (e) {
      toast.error("Could not block");
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (iso) => {
    try {
      await api.delete(`/availability/block/${iso}`);
      setBlocked((b) => b.filter((d) => d !== iso));
      toast.success(`Unblocked ${iso}`);
    } catch (e) {
      toast.error("Could not unblock");
    }
  };

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <AdminNav meName={me?.name} />
      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="mono-eyebrow">§ Availability</div>
          <h1 className="serif text-4xl md:text-5xl mt-2" style={{ letterSpacing: "-0.02em" }}>
            When shall the door be open?
          </h1>
          <p className="mt-3 text-sm" style={{ color: "var(--mute)" }}>
            Set weekly hours below. Booked slots are removed automatically. Use the calendar to block a specific day (leave, travel, etc.).
          </p>
        </motion.div>

        {/* Weekly grid */}
        <section className="border hairline mb-10" style={{ background: "#fff" }}>
          <div className="border-b hairline p-5 flex items-center justify-between">
            <div className="serif text-2xl">Weekly hours</div>
            <button onClick={saveWeekly} disabled={saving} className="btn-terra !py-2 !px-4 !text-[11px]" data-testid="save-weekly-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
          <div className="p-5 space-y-4">
            {DAYS.map(([key, label]) => (
              <div key={key} className="grid grid-cols-12 gap-3 items-start border-b hairline pb-4">
                <div className="col-span-12 md:col-span-2">
                  <div className="serif text-lg" style={{ letterSpacing: "-0.01em" }}>{label}</div>
                  <div className="mono-eyebrow mt-1" style={{ color: "var(--mute)" }}>
                    {(weekly[key] || []).length} slots
                  </div>
                </div>
                <div className="col-span-12 md:col-span-10 flex flex-wrap gap-2">
                  {PRESET_SLOTS.map((s) => {
                    const active = (weekly[key] || []).includes(s);
                    return (
                      <button
                        key={s}
                        type="button"
                        data-testid={`weekly-${key}-${s.replace(/[^A-Za-z0-9]/g, "")}`}
                        onClick={() => toggleSlot(key, s)}
                        className="px-3 py-2 text-xs border transition-colors"
                        style={{
                          borderColor: active ? "var(--ink)" : "var(--line)",
                          background: active ? "var(--ink)" : "#fff",
                          color: active ? "var(--cream)" : "var(--ink)",
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Block specific dates */}
        <section className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-6 border hairline p-6" style={{ background: "#fff" }}>
            <div className="serif text-2xl mb-4">Block a specific date</div>
            <div className="border hairline p-3" style={{ background: "var(--paper)" }} data-testid="block-calendar">
              <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} className="rounded-none" />
            </div>
            <button onClick={blockSelected} disabled={busy || !date} className="btn-terra w-full justify-center mt-4" data-testid="block-date-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
              Block selected date
            </button>
          </div>

          <div className="col-span-12 md:col-span-6 border hairline p-6" style={{ background: "#fff" }}>
            <div className="serif text-2xl mb-4">Blocked dates</div>
            {blocked.length === 0 && (
              <div className="text-sm" style={{ color: "var(--mute)" }}>None yet. Every regular slot is open.</div>
            )}
            <div className="space-y-2">
              {blocked.map((d) => (
                <div key={d} className="flex items-center justify-between border hairline p-3" data-testid={`blocked-${d}`}>
                  <div className="serif text-lg">{d}</div>
                  <button
                    onClick={() => unblock(d)}
                    className="mono-eyebrow inline-flex items-center gap-1"
                    style={{ color: "var(--terra)" }}
                    data-testid={`unblock-${d}`}
                  >
                    <X size={12} /> Unblock
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function sortSlot(a, b) {
  const toMin = (s) => {
    const [t, ampm] = s.split(" ");
    let [h, m] = t.split(":").map(Number);
    if (ampm === "PM" && h !== 12) h += 12;
    if (ampm === "AM" && h === 12) h = 0;
    return h * 60 + m;
  };
  return toMin(a) - toMin(b);
}
